import { Box, Card, Grid, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import {
  getFiftyStates,
  getPlatformVendorsList,
  getReduxTransitionPageData,
  getStaffSubTypes,
  getStaffTypes,
  getStatusConfig,
  getTransitionVendorsList,
  getUserPermissions,
  outsourcedCompanyCategories,
  getOwnershipTypesList,
  getContractedRateTypesList,
  getVehicleDepartmentTypesList,
  getTransportationUserAccessTypesList,
} from '../../../../redux/slices/transitions';
import AccessDenied from '../../../../reusable-components/accessDenied/AccessDenied';
import { enqueueSnackbar } from '../../../../reusable-components/snackbar';
import CanceledAlert from '../CanceledAlert';
import SaveButton from '../SaveButton';
import SkeletonBlock from '../SkeletonBlock';
import { cardStyle, selectedTabStyle } from '../TransitionStyles';
import UpdateStatusButtons from '../UpdateStatusButtons';
import FormList from './FormList';
import { formComponentMap } from './formComponentMap';
import FormsDropdown from '../FormsDropdown';
import BackToHomeButton from '../BackToHomeButton';
import { PAGE_HEIGHT } from '../constants';
import { TransitionViewContext } from './_TransitionViewContext';

export default function TransitionView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState();
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [activeFormId, setActiveFormId] = useState();
  const [formData, setFormData] = useState(undefined);
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const { setDiffDeptStaffCache } = useContext(TransitionViewContext);
  const {
    data: {
      statusConfig,
      userPermissions,
      staffTypes,
      staffSubTypes,
      fiftyStates,
      transitionVendors,
      platformVendors,
      outsourcedCompanyCategoriesList,
      ownershipTypesList,
      contractedRateTypesList,
      vehicleDepartmentsTypesList,
      transportationUserAccessTypesList,
    },
  } = useSelector((state) => state.transitions);

  const saveFormRef = useRef();
  const formSubmit = (allData) => {
    saveFormRef.current.submitForm(allData);
  };

  useEffect(() => {
    if (statusConfig == null) dispatch(getStatusConfig());
    if (staffTypes?.length === 0) dispatch(getStaffTypes());
    if (staffSubTypes?.length === 0) dispatch(getStaffSubTypes());
    if (fiftyStates?.length === 0) dispatch(getFiftyStates());
    if (platformVendors?.length === 0) dispatch(getPlatformVendorsList());
    if (outsourcedCompanyCategoriesList?.length === 0) dispatch(outsourcedCompanyCategories());
    if (ownershipTypesList?.length === 0) dispatch(getOwnershipTypesList());
    if (contractedRateTypesList?.length === 0) dispatch(getContractedRateTypesList());
    if (vehicleDepartmentsTypesList?.length === 0) dispatch(getVehicleDepartmentTypesList());
    if (transportationUserAccessTypesList?.length === 0) dispatch(getTransportationUserAccessTypesList());
  }, []);

  useEffect(() => {
    setDiffDeptStaffCache(new Map());
  }, [activeFormId]);

  useEffect(() => {
    if (!userPermissions[id]?.[facilityId]) dispatch(getUserPermissions(id));
  }, [facilityId, id]);

  const methods = useForm({ defaultValues: { ...formData } });

  useEffect(() => {
    if (data?.companyId && !transitionVendors[data?.companyId]) dispatch(getTransitionVendorsList(data?.companyId));
  }, [data?.companyId]);

  const FormComponent = useMemo(() => formComponentMap[activeFormId] ?? null, [activeFormId]);

  const tabIndex = useMemo(() => {
    let idx = 0;
    if (data?.facilityTabs && facilityId) {
      idx = data.facilityTabs?.findIndex((fac) => String(fac.sellerFacilityId) === String(facilityId));
    }

    return idx !== -1 ? idx : 0;
  }, [data, facilityId]);

  const isFormViewable = useMemo(() => {
    const viewableFacilityIds = data?.formTitles.find((x) => x.formId === activeFormId)?.viewableFacilityIds;
    return data?.isAdmin || viewableFacilityIds?.includes(Number(facilityId));
  }, [facilityId, activeFormId]);

  useEffect(() => {
    dispatch(getReduxTransitionPageData(id))
      .then((resp) => {
        setData(resp);
        setActiveFormId(resp.formTitles[0]?.formId);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoadingPage(false);
      });
  }, [id]);

  useEffect(() => {
    if (formData) {
      methods.reset({ ...formData, companyId: data?.companyId });
      if (
        formData?.sellerFacilityName &&
        data?.facilityTabs.find((x) => String(x.sellerFacilityId) === String(facilityId))?.facilityName !==
          formData?.sellerFacilityName
      ) {
        dispatch(getReduxTransitionPageData(id))
          .then((resp) => setData(resp))
          .catch(() => {
            enqueueSnackbar('Failed to reload data', { variant: 'error' });
          });
      }
    }
  }, [formData]);

  const handleTabChange = (index) => {
    if (index === tabIndex) return;

    const facId = data?.facilityTabs[index]?.sellerFacilityId;
    if (facId) {
      navigate(
        `/dashboard/transitions/transition-form/${id}?facilityId=${facId}`,
        { replace: true } //avoids polluting history
      );
    }
  };

  const tabDisabledMessage = (facIdx) => {
    if (tabIndex !== facIdx && methods.formState.isDirty) return 'Save changes before changing tabs';
    return null;
  };

  return (
    <>
      {isLoadingPage ? (
        <Grid container spacing={2}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={2}>
            <SkeletonBlock height={240} />
            <SkeletonBlock height={90} sx={{ mt: 2 }} />
          </Grid>

          {/* MAIN CONTENT */}
          <Grid item xs={12} md={8}>
            <SkeletonBlock height={60} />
            <SkeletonBlock height={520} sx={{ mt: 2 }} />
          </Grid>

          {/* RIGHT PANEL */}
          <Grid item xs={12} md={2}>
            <SkeletonBlock height={120} />
            <SkeletonBlock height={340} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(formSubmit)}>
            <Grid sx={selectedTabStyle}>
              <BackToHomeButton />
              <Tabs selectedIndex={tabIndex} onSelect={handleTabChange}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={2}>
                    <TabList style={{ borderBottom: '0' }}>
                      <Card sx={cardStyle}>
                        <Typography variant="h6" color="info.main">
                          {data?.portfolioName}
                        </Typography>
                        {data?.facilityTabs?.map((facility, index) => {
                          const disabledMessage = tabDisabledMessage(index);
                          return (
                            <Tab
                              key={facility.sellerFacilityId}
                              style={{ border: 0, width: '100%', wordBreak: 'break-word' }}
                              disabled={!!disabledMessage}
                            >
                              <Tooltip
                                placement="right"
                                arrow
                                title={disabledMessage}
                                disableHoverListener={!disabledMessage}
                              >
                                <span>{facility?.facilityName}</span>
                              </Tooltip>
                            </Tab>
                          );
                        })}
                      </Card>
                      <Card sx={cardStyle}> Collaborator </Card>
                    </TabList>
                  </Grid>

                  <Grid item xs={12} sm={12} md={10}>
                    {data?.facilityTabs?.map((facility) => (
                      <TabPanel key={facility.sellerFacilityId}>
                        <Grid container spacing={2} sx={{ height: PAGE_HEIGHT }}>
                          <Grid
                            item
                            xs={12}
                            sm={12}
                            md={9.7}
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box sx={{ flexShrink: 0 }}>
                              <CanceledAlert />
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>
                              <FormsDropdown
                                formName="Transitions"
                                subFormName={data?.formTitles.find((f) => f.formId === activeFormId)?.formName}
                              />
                            </Box>

                            <Box
                              className="item-scrollbar"
                              sx={{
                                flexGrow: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                              }}
                            >
                              {isFormViewable && FormComponent ? (
                                <FormComponent ref={saveFormRef} setFormData={setFormData} />
                              ) : (
                                <AccessDenied />
                              )}
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={12} md={2.3} sx={{ height: '100%' }}>
                            <Box className="hover-scrollbar" sx={{ height: '100%', overflowY: 'auto', width: '100%' }}>
                              <Card sx={cardStyle}>
                                <SaveButton />
                                <UpdateStatusButtons
                                  data={data}
                                  setData={setData}
                                  proceedButtonLabel="Complete Transition"
                                />
                              </Card>
                              <FormList
                                formTitles={data?.formTitles}
                                activeFormId={activeFormId}
                                setActiveFormId={setActiveFormId}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </TabPanel>
                    ))}
                  </Grid>
                </Grid>
              </Tabs>
            </Grid>
          </form>
        </FormProvider>
      )}
    </>
  );
}
